import React, { useEffect, useState } from "react";
import { Wallet, IndianRupee, Plus, MessageSquare, MessageCircle, KeyRound, X, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { payViaRazorpay } from "@/lib/razorpay";

const PRESETS = [500, 1000, 2500, 5000];

export default function AdminOtpWallet() {
  const { user, tenant } = useAuth();
  const [data, setData] = useState(null);
  const [showRecharge, setShowRecharge] = useState(false);
  const [showRzpKeys, setShowRzpKeys] = useState(false);

  const load = () => api.get("/payments/otp-wallet").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const onRecharge = async (amount) => {
    setShowRecharge(false);
    try {
      const res = await payViaRazorpay({
        amount, purpose: "otp_wallet",
        name: tenant?.brand_name || "OTP Wallet Recharge",
        user: { name: user?.full_name, phone: user?.phone },
      });
      if (res.mocked) toast.success(`(MOCK) Recharge of ₹${amount} captured`);
      else toast.success(`Recharge of ₹${amount} captured`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Payment failed"); }
  };

  if (!data) return <div className="p-6 text-center text-slate-500">Loading...</div>;

  const last30 = (data.ledger || []).slice(0, 30);
  const debits = (data.ledger || []).filter((l) => l.type === "debit").length;
  const credits = (data.ledger || []).filter((l) => l.type === "credit").length;

  return (
    <div className="space-y-5" data-testid="admin-otp-wallet-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">OTP Wallet</h1>
          <p className="text-sm text-slate-500">Used for SMS & WhatsApp OTPs sent to your customers and agents.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="open-rzp-keys" onClick={() => setShowRzpKeys(true)} className="inline-flex items-center gap-2 bg-white border border-slate-200 px-3 py-2 rounded-xl text-[12px] font-bold">
            <KeyRound className="w-3.5 h-3.5" /> Tenant Razorpay
          </button>
          <button data-testid="open-recharge" onClick={() => setShowRecharge(true)} className="inline-flex items-center gap-2 bg-tenant-primary text-white px-4 py-2.5 rounded-xl font-bold text-sm">
            <Plus className="w-4 h-4" /> Recharge
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card grad="emerald-grad" icon={Wallet} label="Available Balance" value={fmtINR(data.balance)} testId="otp-balance" />
        <Card grad="bg-tenant-gradient" icon={MessageSquare} label="Per SMS" value={`₹${data.sms_otp_price}`} testId="otp-sms-price" />
        <Card grad="bg-emerald-600" icon={MessageCircle} label="Per WhatsApp" value={`₹${data.whatsapp_otp_price}`} testId="otp-wa-price" />
      </div>

      <div className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-extrabold">Ledger</h2>
          <div className="text-[11px] text-slate-500">{credits} credits · {debits} debits</div>
        </div>
        <div className="table-scroll">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Remarks</th>
                <th className="text-right p-3">Amount</th>
                <th className="text-right p-3">Closing</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {last30.map((l) => (
                <tr key={l.id} data-testid={`ledger-${l.id}`} className="hover:bg-slate-50">
                  <td className="p-3 text-[12px]">{fmtDate(l.created_at)}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full ${l.type === "credit" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"}`}>{l.type}</span>
                  </td>
                  <td className="p-3 text-[12px] text-slate-700">{l.remarks}</td>
                  <td className={`p-3 text-right font-extrabold ${l.type === "credit" ? "text-emerald-600" : "text-rose-600"}`}>
                    {l.type === "credit" ? "+" : "−"}{fmtINR(l.amount)}
                  </td>
                  <td className="p-3 text-right font-bold text-slate-600">{fmtINR(l.closing)}</td>
                </tr>
              ))}
              {last30.length === 0 && <tr><td colSpan={5} className="text-center p-6 text-slate-400">No ledger entries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {showRecharge && (
        <RechargeModal balance={data.balance} onClose={() => setShowRecharge(false)} onPay={onRecharge} />
      )}
      {showRzpKeys && (
        <TenantRazorpayModal onClose={() => setShowRzpKeys(false)} />
      )}
    </div>
  );
}

const Card = ({ grad, icon: Icon, label, value, testId }) => (
  <div className={`${grad} text-white rounded-2xl p-5 card-shadow`}>
    <Icon className="w-6 h-6" />
    <div className="text-[10px] uppercase tracking-[0.25em] opacity-90 font-bold mt-2">{label}</div>
    <div data-testid={testId} className="text-2xl font-extrabold tracking-tight mt-1">{value}</div>
  </div>
);

const RechargeModal = ({ balance, onClose, onPay }) => {
  const [amount, setAmount] = useState(1000);

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={onClose}>
      <div data-testid="recharge-modal" className="bg-white rounded-3xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-extrabold text-lg">Recharge OTP Wallet</div>
            <div className="text-[11px] text-slate-500">Current balance: <b className="text-emerald-600">{fmtINR(balance)}</b></div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {PRESETS.map((p) => (
            <button
              key={p}
              data-testid={`preset-${p}`}
              onClick={() => setAmount(p)}
              className={`py-3 rounded-xl text-sm font-bold ${amount === p ? "bg-tenant-primary text-white" : "bg-slate-100"}`}
            >
              ₹{p.toLocaleString("en-IN")}
            </button>
          ))}
        </div>

        <label className="mt-4 block text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Custom Amount</label>
        <div className="mt-1.5 flex items-center gap-2 border-2 border-slate-200 rounded-2xl px-3 py-2.5">
          <IndianRupee className="w-4 h-4 text-slate-400" />
          <input data-testid="custom-amount" type="number" min="10" value={amount} onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} className="flex-1 outline-none font-extrabold" />
        </div>

        <button
          data-testid="pay-now"
          onClick={() => amount >= 10 ? onPay(amount) : toast.error("Min ₹10")}
          className="mt-5 w-full bg-emerald-600 text-white py-3 rounded-2xl font-bold inline-flex items-center justify-center gap-2"
        >
          Pay ₹{(amount || 0).toLocaleString("en-IN")} via Razorpay
        </button>
        <p className="text-[10px] text-slate-400 mt-2 text-center">If Razorpay keys are not yet configured, a MOCK capture will simulate success for testing.</p>
      </div>
    </div>
  );
};

const TenantRazorpayModal = ({ onClose }) => {
  const [form, setForm] = useState({});
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/payments/tenant-razorpay").then((r) => { setData(r.data); setForm({}); }); }, []);

  const cur = (k) => (form[k] !== undefined ? form[k] : (data?.[k] ?? ""));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (Object.keys(form).length === 0) return toast.info("Nothing changed");
    setSaving(true);
    try {
      const { data: res } = await api.patch("/payments/tenant-razorpay", form);
      setData(res); setForm({});
      toast.success("Razorpay keys saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (!data) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={onClose}>
      <div data-testid="tenant-rzp-modal" className="bg-white rounded-3xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="font-extrabold text-lg">Tenant Razorpay Keys</div>
            <div className="text-[11px] text-slate-500">Used to receive payments from your customers (e.g., Saving Wallet topups).</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-[11px] text-amber-700 flex gap-2 items-start my-3">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>Use rzp_test_* keys during testing. Switch to live only when you're ready to accept real money.</div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Mode</label>
            <select data-testid="t-rzp-mode" value={cur("razorpay_mode")} onChange={(e) => set("razorpay_mode", e.target.value)} className="mt-1.5 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none bg-white">
              <option value="test">Test</option>
              <option value="live">Live</option>
            </select>
          </div>
          <ModalField testId="t-rzp-key-id" label="Key ID" placeholder="rzp_test_xxxxxxxx" value={cur("razorpay_key_id")} onChange={(v) => set("razorpay_key_id", v)} />
          <ModalField testId="t-rzp-key-secret" label="Key Secret" placeholder="Leave blank to keep current" masked value={cur("razorpay_key_secret")} onChange={(v) => set("razorpay_key_secret", v)} />
          <ModalField testId="t-rzp-webhook" label="Webhook Secret" placeholder="Leave blank to keep current" masked value={cur("razorpay_webhook_secret")} onChange={(v) => set("razorpay_webhook_secret", v)} />
          <button data-testid="save-t-rzp" onClick={save} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-tenant-primary text-white py-3 rounded-2xl font-bold disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Keys"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ModalField = ({ label, value, onChange, testId, placeholder, masked }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{label}</label>
    <input
      data-testid={testId}
      type={masked ? "password" : "text"}
      autoComplete="new-password"
      value={value || ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-tenant-primary font-mono text-[12px]"
    />
  </div>
);
